import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import PermissionHide from 'components/PermissionHide';
import { saveAs } from 'file-saver';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { tap } from 'rxjs/operators';
import ValueCard from 'v2/components/Shared/ValueCard';
import { ECancelChartTypes } from 'v2/interfaces/reports/cancel';
import { cancelService } from 'v2/services/reports/cancel';

import type { ICardProps } from 'v2/components/Shared/ValueCard';

import { selectTypeChart } from './style';

interface ICustomLabel {
  x: number;
  y: number;
  width: number;
  height: number;
  value: number;
}

interface IChartData {
  name: string;
  value: number;
}

function ChartTab() {
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [typeChart, setTypeChart] = useState<string>(ECancelChartTypes.PERCENTAGE);
  const [chartData] = useObservable(() => cancelService.getChart(), []);
  const [isLoading] = useObservable(() => cancelService.isLoading(), []);
  const [handleExport] = useCallbackObservable(() => {
    setLoadingExport(true);
    return cancelService.export().pipe(
      tap(
        (data) => {
          try {
            const dateExport = moment(new Date()).format('DD-MM-YYYY');
            let fileExport = new Blob([data], { type: 'text/csv; charset=utf-8' });
            saveAs(fileExport, `Contratos_MotivosCancelamento-${dateExport}.csv`);
          } catch (e) {
            console.log(e);
          } finally {
            setLoadingExport(false);
          }
        },
        (err) => {
          setLoadingExport(false);
          toast.error('Algo de errado aconteceu, tente exportar novamente.');
          console.log(err);
        }
      )
    );
  }, []);

  const chart = chartData?.chart || [];
  const subscriptions_amount = chartData?.subscriptions_amount || 0;
  const subscriptions_total = chartData?.subscriptions_total || 0;

  const incomeCards: ICardProps[] = useMemo(
    () => [
      {
        title: 'Total de cancelamentos',
        total: !!isLoading ? '-' : subscriptions_total,
        helpText: 'Quantia total de contratos cancelados',
      },
      {
        title: 'Valor total',
        total: !!isLoading ? '-' : <ColoredFormattedNumber value={subscriptions_amount} format='BRL' />,
        helpText: 'Valor total de contratos cancelados',
      },
    ],
    [isLoading, subscriptions_amount, subscriptions_total]
  );

  const handleChangeChart = (
    event: React.ChangeEvent<{
      name?: string;
      value: string;
    }>
  ) => {
    return setTypeChart(event.target.value);
  };

  const data: Array<IChartData> = chart.map((item) => ({
    name: item.reason,
    value: typeChart === ECancelChartTypes.PERCENTAGE ? item.percentage : item.amount,
  }));

  const CustomizedLabel = ({ x, y, width, height, value }: ICustomLabel) => {
    const formatValue = (valueToFormat: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valueToFormat);
    const formattedValue = typeChart === ECancelChartTypes.PERCENTAGE ? `${value}%` : formatValue(value);
    return (
      <text x={x + width + 10} y={y + height / 2 + 10} fill='#666' textAnchor='right' dy={-6}>
        {formattedValue}
      </text>
    );
  };

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: '10px' }}>
        {incomeCards.map((card) => (
          <Grid xs={12} sm={6} md={3} key={card.title} item>
            <ValueCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid xs={12} item>
          <Card style={{ padding: '20px' }} elevation={0}>
            <Grid container>
              {data.length === 0 ? (
                <Grid xs={12}>
                  <Typography variant='body1' align='center'>
                    Nenhum registro encontrado.
                  </Typography>
                </Grid>
              ) : (
                <>
                  <Grid xs={12} sm={6} md={3} item style={selectTypeChart}>
                    <FormControl fullWidth>
                      <Select labelId='typeChart' id='typeChartSelect' value={typeChart} label='Alterar gráfico' onChange={handleChangeChart}>
                        <MenuItem value={ECancelChartTypes.PERCENTAGE}>Motivos por valor (%)</MenuItem>
                        <MenuItem value={ECancelChartTypes.CURRENCY}>Motivos por valor</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <PermissionHide action={'myeduzz:reports:exportar'}>
                    <Grid xs={12} sm={6} md={9} item style={{ textAlign: 'right', color: '#192542' }}>
                      <Button
                        disabled={!!isLoading || !!loadingExport}
                        onClick={handleExport}
                        variant='text'
                        startIcon={isLoading || loadingExport ? <CircularProgress size={20} color='inherit' /> : <SystemUpdateAltIcon />}
                      >
                        Exportar
                      </Button>
                    </Grid>
                  </PermissionHide>

                  <Grid xs={12} style={{ height: 400 }}>
                    <ResponsiveContainer width='100%' height='100%'>
                      <BarChart layout='vertical' data={data} barCategoryGap='25%' margin={{ top: 30 }}>
                        <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                        <XAxis type='number' axisLine={false} domain={[0, `dataMax + ${Math.max(...data.map((i) => i.value)) * 0.3}`]} hide />
                        <YAxis type='category' dataKey='name' width={450} />
                        <Bar dataKey='value' fill='#8884d8' isAnimationActive={false} label={CustomizedLabel} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                </>
              )}
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

export default ChartTab;
